// Settings page for application configuration

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Alert,
  Divider,
  Avatar,
  Chip,
  Slider,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Save as SaveIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Language as LanguageIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PhoneIphone as MobileIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Mock settings state
  const [settings, setSettings] = useState({
    // Company settings
    companyName: 'Demo Firma Sp. z o.o.',
    nip: '1234567890',
    address: 'ul. Przykładowa 123, 00-001 Warszawa',
    monthlyBudget: 10000,
    currency: 'PLN',
    
    // Notification settings
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    lowStockAlerts: true,
    budgetAlerts: true,
    orderConfirmations: true,
    budgetThreshold80: true,
    budgetThreshold90: true,
    budgetThreshold100: true,
    
    // Auto-ordering settings
    autoOrderEnabled: true,
    autoOrderThreshold: 20,
    autoOrderQuantity: 50,
    
    // UI settings
    theme: 'light',
    language: 'pl',
    defaultView: 'dashboard',
    
    // Security settings
    sessionTimeout: 480, // minutes
    twoFactorEnabled: false,
    passwordExpiry: 90, // days
    
    // Integration settings
    biuropaczkaApiKey: '•••••••••••••••••••••••••',
    biuropaczkaSync: true,
    syncInterval: 24 // hours
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    // Mock save functionality
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" fontWeight="bold">
            Ustawienia
          </Typography>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            size="large"
            onClick={handleSave}
          >
            Zapisz zmiany
          </Button>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Konfiguracja aplikacji, powiadomień i integracji
        </Typography>
      </Box>

      {/* Success Alert */}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Ustawienia zostały zapisane pomyślnie!
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab icon={<BusinessIcon />} label="Firma" />
          <Tab icon={<NotificationsIcon />} label="Powiadomienia" />
          <Tab icon={<ScheduleIcon />} label="Automatyzacja" />
          <Tab icon={<PaletteIcon />} label="Interfejs" />
          {isAdmin && <Tab icon={<SecurityIcon />} label="Bezpieczeństwo" />}
          {isAdmin && <Tab icon={<MoneyIcon />} label="Integracje" />}
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Company Settings */}
          <Typography variant="h6" gutterBottom>
            Dane firmy
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nazwa firmy"
                value={settings.companyName}
                onChange={(e) => handleSettingChange('companyName', e.target.value)}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="NIP"
                value={settings.nip}
                onChange={(e) => handleSettingChange('nip', e.target.value)}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adres"
                value={settings.address}
                onChange={(e) => handleSettingChange('address', e.target.value)}
                disabled={!isAdmin}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Budżet
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Miesięczny budżet"
                type="number"
                value={settings.monthlyBudget}
                onChange={(e) => handleSettingChange('monthlyBudget', parseInt(e.target.value))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">zł</InputAdornment>,
                }}
                disabled={!isAdmin}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!isAdmin}>
                <InputLabel>Waluta</InputLabel>
                <Select
                  value={settings.currency}
                  label="Waluta"
                  onChange={(e) => handleSettingChange('currency', e.target.value)}
                >
                  <MenuItem value="PLN">PLN (Złoty polski)</MenuItem>
                  <MenuItem value="EUR">EUR (Euro)</MenuItem>
                  <MenuItem value="USD">USD (Dolar amerykański)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Dane firmy są używane w fakturach i dokumentach. Zmiany wymagają uprawnień administratora.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Notification Settings */}
          <Typography variant="h6" gutterBottom>
            Powiadomienia
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Kanały powiadomień
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Powiadomienia email"
                    secondary="Otrzymuj powiadomienia na adres email"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <SmsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Powiadomienia SMS"
                    secondary="Otrzymuj krytyczne powiadomienia przez SMS"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.smsNotifications}
                      onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <MobileIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Powiadomienia push"
                    secondary="Wyświetlaj powiadomienia w przeglądarce"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Typy powiadomień
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.lowStockAlerts}
                    onChange={(e) => handleSettingChange('lowStockAlerts', e.target.checked)}
                  />
                }
                label="Alerty o niskich stanach magazynowych"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.budgetAlerts}
                    onChange={(e) => handleSettingChange('budgetAlerts', e.target.checked)}
                  />
                }
                label="Alerty budżetowe"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.orderConfirmations}
                    onChange={(e) => handleSettingChange('orderConfirmations', e.target.checked)}
                  />
                }
                label="Potwierdzenia zamówień"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Progi alertów budżetowych
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.budgetThreshold80}
                    onChange={(e) => handleSettingChange('budgetThreshold80', e.target.checked)}
                  />
                }
                label="Powiadom przy 80% budżetu"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.budgetThreshold90}
                    onChange={(e) => handleSettingChange('budgetThreshold90', e.target.checked)}
                  />
                }
                label="Powiadom przy 90% budżetu"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.budgetThreshold100}
                    onChange={(e) => handleSettingChange('budgetThreshold100', e.target.checked)}
                  />
                }
                label="Powiadom przy przekroczeniu budżetu"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Auto-ordering Settings */}
          <Typography variant="h6" gutterBottom>
            Automatyczne zamówienia
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoOrderEnabled}
                    onChange={(e) => handleSettingChange('autoOrderEnabled', e.target.checked)}
                  />
                }
                label="Włącz automatyczne zamówienia"
              />
              <Typography variant="body2" color="text.secondary">
                System automatycznie złoży zamówienie gdy stan produktu spadnie poniżej minimalnego poziomu
              </Typography>
            </Grid>
            
            {settings.autoOrderEnabled && (
              <>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>
                    Próg automatycznego zamówienia: {settings.autoOrderThreshold}%
                  </Typography>
                  <Slider
                    value={settings.autoOrderThreshold}
                    onChange={(e, value) => handleSettingChange('autoOrderThreshold', value)}
                    min={10}
                    max={50}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Zamów automatycznie gdy stan spadnie do tego poziomu względem maksymalnego
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>
                    Ilość automatycznego zamówienia: {settings.autoOrderQuantity}%
                  </Typography>
                  <Slider
                    value={settings.autoOrderQuantity}
                    onChange={(e, value) => handleSettingChange('autoOrderQuantity', value)}
                    min={20}
                    max={100}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Zamów tyle procent maksymalnego stanu magazynowego
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Ważne:</strong> Automatyczne zamówienia wymagają aktywnej integracji z BiuroPaczka 
                      i wystarczającego salda na koncie. Wszystkie automatyczne zamówienia są logowane i mogą być anulowane.
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* UI Settings */}
          <Typography variant="h6" gutterBottom>
            Personalizacja interfejsu
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Motyw</InputLabel>
                <Select
                  value={settings.theme}
                  label="Motyw"
                  onChange={(e) => handleSettingChange('theme', e.target.value)}
                >
                  <MenuItem value="light">Jasny</MenuItem>
                  <MenuItem value="dark">Ciemny</MenuItem>
                  <MenuItem value="auto">Automatyczny</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Język</InputLabel>
                <Select
                  value={settings.language}
                  label="Język"
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                >
                  <MenuItem value="pl">Polski</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="de">Deutsch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Domyślny widok</InputLabel>
                <Select
                  value={settings.defaultView}
                  label="Domyślny widok"
                  onChange={(e) => handleSettingChange('defaultView', e.target.value)}
                >
                  <MenuItem value="dashboard">Dashboard</MenuItem>
                  <MenuItem value="products">Produkty</MenuItem>
                  <MenuItem value="orders">Zamówienia</MenuItem>
                  <MenuItem value="analytics">Raporty</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Zmiany w interfejsie zostaną zastosowane po odświeżeniu strony.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        {isAdmin && (
          <TabPanel value={activeTab} index={4}>
            {/* Security Settings */}
            <Typography variant="h6" gutterBottom>
              Bezpieczeństwo
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Timeout sesji (minuty)"
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                />
                <Typography variant="caption" color="text.secondary">
                  Automatyczne wylogowanie po okresie nieaktywności
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Wygaśnięcie hasła (dni)"
                  type="number"
                  value={settings.passwordExpiry}
                  onChange={(e) => handleSettingChange('passwordExpiry', parseInt(e.target.value))}
                />
                <Typography variant="caption" color="text.secondary">
                  Wymuszenie zmiany hasła po tym okresie
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.twoFactorEnabled}
                      onChange={(e) => handleSettingChange('twoFactorEnabled', e.target.checked)}
                    />
                  }
                  label="Uwierzytelnianie dwuskładnikowe (2FA)"
                />
                <Typography variant="body2" color="text.secondary">
                  Dodatkowa warstwa bezpieczeństwa dla kont administratorów
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setPasswordDialogOpen(true)}
                >
                  Zmień hasło
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Uwaga:</strong> Zmiany ustawień bezpieczeństwa dotyczą wszystkich użytkowników w systemie.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>
        )}

        {isAdmin && (
          <TabPanel value={activeTab} index={5}>
            {/* Integration Settings */}
            <Typography variant="h6" gutterBottom>
              Integracje
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        B
                      </Avatar>
                      <Box>
                        <Typography variant="h6">BiuroPaczka.pl</Typography>
                        <Chip 
                          label={settings.biuropaczkaSync ? "Połączone" : "Rozłączone"}
                          color={settings.biuropaczkaSync ? "success" : "error"}
                          size="small"
                        />
                      </Box>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          label="Klucz API"
                          type={showPassword ? 'text' : 'password'}
                          value={settings.biuropaczkaApiKey}
                          onChange={(e) => handleSettingChange('biuropaczkaApiKey', e.target.value)}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Interwał synchronizacji (godziny)"
                          type="number"
                          value={settings.syncInterval}
                          onChange={(e) => handleSettingChange('syncInterval', parseInt(e.target.value))}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.biuropaczkaSync}
                              onChange={(e) => handleSettingChange('biuropaczkaSync', e.target.checked)}
                            />
                          }
                          label="Automatyczna synchronizacja produktów i cen"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Informacja:</strong> Klucz API można uzyskać w panelu BiuroPaczka.pl w sekcji "Integracje". 
                    Synchronizacja odbywa się automatycznie co {settings.syncInterval} godzin.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>
        )}
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Zmiana hasła</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Obecne hasło"
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nowe hasło"
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Potwierdź nowe hasło"
                type="password"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            Anuluj
          </Button>
          <Button variant="contained">
            Zmień hasło
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}